import { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faChevronDown,
	faChevronUp,
	faPlus,
	faPen,
	faTrash,
} from "@fortawesome/pro-regular-svg-icons";

import { useAppSelector } from "@/redux/hooks";
import { LoadingOverlay } from "@/components/LoadingOverlay/LoadingOverlay";
import { Button } from "@/components/Button/Button";
import { Toggle } from "@/components/Toggle/Toggle";
import { Icon } from "@/components/Icon/Icon";
import { useSnackBar } from "@/utils/useSnackbar";
import { formatFrameworkName } from "@/utils/formatFrameworkName";
import { ButtonSize } from "@/components/Button/types";

import {
	useGetCopywritingFrameworksQuery,
	useToggleCopywritingFrameworkMutation,
	useDeleteCopywritingFrameworkMutation,
} from "@/pages/Private/redux/copywritingFramework/copywritingFramework.api";
import { copywritingFrameworkSelector } from "@/pages/Private/redux/copywritingFramework/copywritingFramework.slice";

import { CopywritingFramework, MessageType } from "../../schema/copywritingFramework";
import { CopywritingFrameworkModal } from "./CopywritingFrameworkModal";

export const CopywritingFrameworks: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`copywritingFrameworks.${key}`), [t]);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { isLoading } = useGetCopywritingFrameworksQuery();
	const { frameworks } = useAppSelector(copywritingFrameworkSelector);
	const [toggleFramework] = useToggleCopywritingFrameworkMutation();
	const [deleteFramework] = useDeleteCopywritingFrameworkMutation();

	const [modalOpen, setModalOpen] = useState(false);
	const [editingFramework, setEditingFramework] = useState<CopywritingFramework | null>(null);

	const handleCreate = () => {
		setEditingFramework(null);
		setModalOpen(true);
	};

	const handleEdit = (framework: CopywritingFramework) => {
		setEditingFramework(framework);
		setModalOpen(true);
	};

	const handleToggle = async (id: number) => {
		try {
			await toggleFramework(id).unwrap();
			handleOpenSnackbar(ts("toggled"));
		} catch {
			handleOpenSnackbar(ts("error"));
		}
	};

	const handleDelete = async (id: number) => {
		try {
			await deleteFramework(id).unwrap();
			handleOpenSnackbar(ts("deleted"));
		} catch {
			handleOpenSnackbar(ts("error"));
		}
	};

	if (isLoading) {
		return <LoadingOverlay />;
	}

	return (
		<div className="flex flex-col gap-6 py-4 pb-24">
			<div className="flex items-center justify-between">
				<div className="flex flex-col gap-1">
					<p className="text-sm text-text-meta">{ts("description")}</p>
				</div>
				<div className="w-fit">
					<Button
						image={<Icon className="text-white mr-1" icon={faPlus} size="sm" />}
						size={ButtonSize.S}
						title={ts("addFramework")}
						onClick={handleCreate}
					/>
				</div>
			</div>

			<div className="flex flex-col gap-3">
				{frameworks.map((fw) => (
					<FrameworkCard
						key={fw.id}
						framework={fw}
						ts={ts}
						onDelete={handleDelete}
						onEdit={handleEdit}
						onToggle={handleToggle}
					/>
				))}
			</div>

			{modalOpen && (
				<CopywritingFrameworkModal
					framework={editingFramework}
					isOpen={modalOpen}
					onClose={() => setModalOpen(false)}
					onSuccess={(msg: string) => handleOpenSnackbar(msg)}
				/>
			)}

			{renderMessageSnackbar()}
		</div>
	);
};

interface FrameworkCardProps {
	framework: CopywritingFramework;
	ts: (key: string) => string;
	onToggle: (id: number) => void;
	onEdit: (framework: CopywritingFramework) => void;
	onDelete: (id: number) => void;
}

const FrameworkCard: FunctionComponent<FrameworkCardProps> = ({
	framework,
	ts,
	onToggle,
	onEdit,
	onDelete,
}) => {
	const [showDetails, setShowDetails] = useState(false);

	return (
		<div className="rounded-xl border border-border-light bg-brand-white p-4 flex flex-col gap-2">
			<div className="flex items-start justify-between gap-4">
				<div className="flex flex-col gap-2 flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<h3 className="text-smd font-medium text-text truncate">
							{formatFrameworkName(framework.name)}
						</h3>
						<span className="text-xs text-text-meta px-2 py-0.5 rounded-full bg-gray-100">
							{framework.isDefault ? ts("default") : ts("custom")}
						</span>
					</div>
					<p className="text-xs text-text-meta line-clamp-2">{framework.description}</p>
					<div className="flex flex-wrap items-center gap-1.5 mt-1">
						{framework.applicableTo.map((type: MessageType) => (
							<span
								key={type}
								className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-brand-light"
							>
								{ts(`messageType.${type}`)}
							</span>
						))}
						<button
							className="inline-flex items-center gap-1 text-xs text-text-meta hover:text-text transition-colors cursor-pointer ml-1"
							type="button"
							onClick={() => setShowDetails(!showDetails)}
						>
							<span>{showDetails ? ts("hideDetails") : ts("showDetails")}</span>
							<Icon
								className="w-3 h-3"
								icon={showDetails ? faChevronUp : faChevronDown}
								size="sm"
							/>
						</button>
					</div>
				</div>

				<div className="flex items-center gap-3 shrink-0">
					<Toggle isChecked={framework.enabled} onChange={() => onToggle(framework.id)} />
					<button
						className="p-1.5 text-text-meta hover:text-text transition-colors cursor-pointer"
						type="button"
						onClick={() => onEdit(framework)}
					>
						<Icon icon={faPen} size="sm" />
					</button>
					{!framework.isDefault && (
						<button
							className="p-1.5 text-text-meta hover:text-status-error transition-colors cursor-pointer"
							type="button"
							onClick={() => onDelete(framework.id)}
						>
							<Icon icon={faTrash} size="sm" />
						</button>
					)}
				</div>
			</div>

			{showDetails && (
				<div className="flex flex-col gap-3 border-t border-border-light pt-3">
					<FrameworkDetail label={ts("form.structure")} text={framework.structure} />
					<FrameworkDetail label={ts("form.whenToUse")} text={framework.whenToUse} />
					<FrameworkDetail label={ts("form.whenNotToUse")} text={framework.whenNotToUse} />
				</div>
			)}
		</div>
	);
};

const FrameworkDetail: FunctionComponent<{ label: string; text?: string | null }> = ({
	label,
	text,
}) => {
	if (!text) {
		return null;
	}

	return (
		<div className="flex flex-col gap-0.5">
			<span className="text-xs font-semibold text-text">{label}</span>
			<p className="text-xs text-text-meta leading-relaxed">{text}</p>
		</div>
	);
};
